import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import Select from 'react-select'
import { toast } from 'react-toastify'
import { SEARCH_RESOURCES_CENTER } from '../../gql/queries/resourceCenterQuery'
import { GET_DOCUMENTS_POLICY } from '../../gql/queries/policiesApiQueries'
import {
  IResourceCenter,
  IIncrementResourceViewsResponse,
  IUpdateResourceCenterResponse,
} from '../../types/resourceCenter'
import { parseId } from '../../helpers'
import { IResourceCategory } from '../../types/resourceCategory'
import { IResAllTagsDocuments } from '../../types/policiesApi'
import { useDisclourse } from '../../hooks/useDisclourse'
import { ModalAddResource } from './components/ModalAddResource'
import { ModalEditResource } from './components/ModalEditResource'
import { ModalDeleteResource } from './components/ModalDeleteResource'
import { ModalViewResource } from './components/ModalViewResource'
import { CategoryTree } from './components/CategoryTree'
import { ModalManageTags } from './components/ModalManageTags'
import { getCategoryPath, buildCategoryChildrenMap } from './categoryTreeHelpers'
import { RenderFilePreview, GridFilePreview } from './components/RenderFilePreview'
import { VideoPreview, parseVideoUrl } from './components/VideoPreview'
import { isVideoFile } from './resourceTypeHelpers'
import { prependBackendUrl } from '../../gql/client'
import moment from 'moment'
import {
  getResourceTypeIcon,
  getResourceTypeBadgeStyle,
  getResourceTypeLabel,
  RESOURCE_TYPE_OPTIONS,
} from './resourceTypeHelpers'
import {
  INCREMENT_RESOURCE_VIEWS,
  UPDATE_RESOURCE_CENTER,
} from '../../gql/mutations/resourceCenterMutations'

interface ISearchResourcesCenterResponse {
  resourcesCenterAdvancedSearch: IResourceCenter[]
}

const ViewsBadge = ({ viewsCount, className = '' }: { viewsCount: number; className?: string }) => {
  const baseClass = 'badge bg-primary text-white d-inline-flex align-items-center gap-1'
  return (
    <span className={`${baseClass} ${className}`.trim()}>
      <i className="bi bi-eye" />
      <span>{viewsCount}</span>
    </span>
  )
}

export const ResourceCenterPage = () => {
  const { onOpen: onOpenAdd, isOpen: isOpenAdd, onClose: onCloseAdd } = useDisclourse()
  const { onOpen: onOpenEdit, isOpen: isOpenEdit, onClose: onCloseEdit } = useDisclourse()
  const { onOpen: onOpenDelete, isOpen: isOpenDelete, onClose: onCloseDelete } = useDisclourse()
  const { onOpen: onOpenView, isOpen: isOpenView, onClose: onCloseView } = useDisclourse()
  const { onOpen: onOpenManageTags, isOpen: isOpenManageTags, onClose: onCloseManageTags } = useDisclourse()

  const [selectedResource, setSelectedResource] = useState<IResourceCenter | null>(null)
  const [filterName, setFilterName] = useState('')
  const [filterType, setFilterType] = useState<string | null>(null)
  const [filterTag, setFilterTag] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid')

  // Carpeta seleccionada en el árbol de categorías
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  // Ahora que las subcarpetas se navegan desde la propia lista, por defecto
  // mostramos solo el contenido directo de la carpeta actual (no aplanado)
  const [includeSubcategories, setIncludeSubcategories] = useState(false)
  const [allCategories, setAllCategories] = useState<IResourceCategory[]>([])
  const [showFolders, setShowFolders] = useState(true)

  const handleCategoriesLoaded = useCallback((categories: IResourceCategory[]) => {
    setAllCategories(categories)
  }, [])

  // Permite refrescar los contadores del árbol (resourcesCount) tras un drop,
  // sin importar si ocurrió en el sidebar o sobre una carpeta dentro de la lista
  const categoryTreeRefetchRef = useRef<() => void>(() => {})
  const handleCategoryTreeRefetchAvailable = useCallback((refetch: () => void) => {
    categoryTreeRefetchRef.current = refetch
  }, [])

  const categoryPath = useMemo(
    () => getCategoryPath(allCategories, selectedCategoryId),
    [allCategories, selectedCategoryId]
  )

  // Subcarpetas directas de la carpeta actual (o raíces si selectedCategoryId es null),
  // para mostrarlas como "carpetas" dentro de la propia lista de contenidos
  const categoryChildrenMap = useMemo(
    () => buildCategoryChildrenMap(allCategories),
    [allCategories]
  )
  const currentFolders = useMemo(
    () => categoryChildrenMap.get(selectedCategoryId) || [],
    [categoryChildrenMap, selectedCategoryId]
  )

  // Carpeta sobre la que se está arrastrando un recurso (highlight de drop target)
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null)

  // Selección de recursos para mover (drag & drop a carpetas)
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([])

  // Card con hover en grid: reproduce el video en silencio (estilo YouTube)
  const [hoveredResourceId, setHoveredResourceId] = useState<string | null>(null)

  const toggleResourceSelection = (resourceId: string) => {
    setSelectedResourceIds((prev) =>
      prev.includes(resourceId)
        ? prev.filter((id) => id !== resourceId)
        : [...prev, resourceId]
    )
  }

  const handleResourceDragStart = (e: React.DragEvent, resource: IResourceCenter) => {
    // Si el recurso arrastrado está seleccionado, se mueven todos los seleccionados
    const ids = selectedResourceIds.includes(resource.id) ? selectedResourceIds : [resource.id]
    e.dataTransfer.setData('application/x-resource-ids', JSON.stringify(ids))
    e.dataTransfer.effectAllowed = 'move'

    // Ghost compacto: una etiqueta con solo el título (o el conteo), en vez del
    // row completo que tapa el árbol y no deja ver qué carpeta recibe el drop
    const ghost = document.createElement('div')
    const icon = document.createElement('i')
    icon.className = ids.length > 1 ? 'bi bi-files me-2' : 'bi bi-file-earmark me-2'
    ghost.appendChild(icon)
    ghost.appendChild(
      document.createTextNode(ids.length > 1 ? `${ids.length} resources` : resource.name)
    )
    Object.assign(ghost.style, {
      position: 'absolute',
      top: '-9999px',
      left: '-9999px',
      padding: '4px 12px',
      backgroundColor: '#fff',
      border: '1px solid #0d6efd',
      borderRadius: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      fontSize: '13px',
      fontWeight: '600',
      color: '#0d6efd',
      whiteSpace: 'nowrap',
      maxWidth: '280px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      pointerEvents: 'none',
    })
    document.body.appendChild(ghost)
    e.dataTransfer.setDragImage(ghost, 12, 14)
    setTimeout(() => document.body.removeChild(ghost), 0)
  }

  // Debounced filter values - se actualizan 500ms después de que el usuario deje de escribir
  const [debouncedFilterName, setDebouncedFilterName] = useState('')
  const [debouncedFilterTag, setDebouncedFilterTag] = useState<string | null>(null)

  // Debounce effect para filterName
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterName(filterName)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [filterName])

  // Debounce effect para filterTag
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterTag(filterTag)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [filterTag])

  // Con un filtro activo (nombre, tipo o tag): en la raíz ("All resources") se
  // busca en toda la organización; dentro de una carpeta, la búsqueda se
  // mantiene acotada a esa carpeta y sus subcarpetas (forzamos includeSubcategories,
  // sin importar el estado del toggle, para no perderse contenido anidado)
  const hasActiveSearch = Boolean(debouncedFilterName || filterType || debouncedFilterTag)

  // Query con filtros debounced que viajan al backend
  const { data, loading, error, refetch } = useQuery<ISearchResourcesCenterResponse>(
    SEARCH_RESOURCES_CENTER,
    {
      variables: {
        name: debouncedFilterName || null,
        resourceType: filterType || null, // filterType no necesita debounce porque es un select
        tagName: debouncedFilterTag || null,
        isActive: true, // Solo recursos activos
        isCampaignMedia: false, // Excluir archivos de campañas (solo Resource Center interno)
        categoryId: selectedCategoryId ? Number(selectedCategoryId) : null,
        includeSubcategories: hasActiveSearch ? true : includeSubcategories,
      },
      fetchPolicy: 'network-only',
    }
  )

  const { data: dataTags } = useQuery<IResAllTagsDocuments>(GET_DOCUMENTS_POLICY, {
    fetchPolicy: 'cache-first',
  })

  const [incrementResourceViews] = useMutation<
    IIncrementResourceViewsResponse,
    { id: number }
  >(INCREMENT_RESOURCE_VIEWS)

  const [updateResourceForMove] = useMutation<IUpdateResourceCenterResponse>(
    UPDATE_RESOURCE_CENTER
  )

  // Los recursos ya vienen filtrados del backend
  const filteredResources = useMemo(() => {
    const results = data?.resourcesCenterAdvancedSearch || []
    // Con una búsqueda activa mostramos resultados en una vista plana (sin el
    // filtro de "sin categoría" de la raíz) — el backend ya se encarga de
    // acotar el alcance: toda la organización en la raíz, o la carpeta actual
    // + sus subcarpetas si hay una seleccionada
    if (hasActiveSearch) {
      return results
    }
    // "All resources" = raíz del árbol: las subcarpetas ya se muestran en el
    // sidebar, así que aquí solo listamos lo que no tiene carpeta asignada —
    // mostrar todo de una vez sería anticlimático y duplicaría lo del árbol
    if (!selectedCategoryId) {
      return results.filter((resource) => !resource.category)
    }
    return results
  }, [data, selectedCategoryId, hasActiveSearch])

  // Pagination
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(1)
  const totalResources = filteredResources.length
  const totalPages = Math.max(1, Math.ceil(totalResources / pageSize))

  useEffect(() => {
    if (page > totalPages) setPage(1)
  }, [filteredResources, pageSize, totalResources, page, totalPages])

  // Paginated resources
  const paginatedResources = useMemo(() => {
    const startIdx = (page - 1) * pageSize
    const endIdx = Math.min(startIdx + pageSize, totalResources)
    return filteredResources.slice(startIdx, endIdx)
  }, [filteredResources, page, pageSize, totalResources])

  const handleEdit = (resource: IResourceCenter) => {
    setSelectedResource(resource)
    onOpenEdit()
  }

  const handleDelete = (resource: IResourceCenter) => {
    setSelectedResource(resource)
    onOpenDelete()
  }

  const handleRefetch = () => {
    refetch()
  }

  const handleSearchClick = () => {
    setDebouncedFilterName(filterName)
    setDebouncedFilterTag(filterTag)
    setPage(1)

    const searching = Boolean(filterName || filterType || filterTag)
    refetch({
      name: filterName || null,
      resourceType: filterType || null,
      tagName: filterTag || null,
      isActive: true,
      categoryId: selectedCategoryId ? Number(selectedCategoryId) : null,
      includeSubcategories: searching ? true : includeSubcategories,
    })
  }

  const handleSelectCategory = (categoryId: string | null) => {
    setSelectedCategoryId(categoryId)
    setPage(1)
  }

  // Mueve los recursos soltados sobre una carpeta del árbol (null = sin categoría)
  const handleDropResources = async (categoryId: string | null, resourceIds: string[]) => {
    const resourcesById = new Map(filteredResources.map((resource) => [resource.id, resource]))
    try {
      await Promise.all(
        resourceIds.map((resourceId) => {
          const resource = resourcesById.get(resourceId)
          if (!resource) return Promise.resolve(null)
          return updateResourceForMove({
            variables: {
              id: parseId(resourceId),
              resourceData: {
                name: resource.name,
                categoryId: categoryId ? parseId(categoryId) : null,
              },
            },
          })
        })
      )
      toast.success(
        resourceIds.length === 1
          ? 'Resource moved successfully'
          : `${resourceIds.length} resources moved successfully`
      )
      setSelectedResourceIds([])
      refetch()
      // Refresca los contadores del árbol (carpeta origen y destino cambian de tamaño)
      categoryTreeRefetchRef.current()
    } catch (err) {
      console.error('Error moving resources:', err)
      toast.error('Error moving resources')
    }
  }

  const triggerIncrementResourceViews = (resourceId: string) => {
    const numericId = Number(resourceId)

    if (Number.isNaN(numericId)) {
      console.warn('No se pudo incrementar las vistas: id inválido', resourceId)
      return
    }

    incrementResourceViews({ variables: { id: numericId } }).catch((mutationError) => {
      console.error('Error incrementando vistas del recurso', mutationError)
    })
  }

  const handleResourceAccess = (resource: IResourceCenter, action: 'view' | 'download' = 'view') => {
    triggerIncrementResourceViews(resource.id)

    if (typeof window === 'undefined') return

    // Si es "view", abrir el modal
    if (action === 'view') {
      setSelectedResource(resource)
      onOpenView()
      return
    }

    // Si es download y tiene file
    if (action === 'download' && resource.file) {
      const resourceUrl = prependBackendUrl(resource.file)
      const anchor = document.createElement('a')
      anchor.href = resourceUrl
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
      anchor.download = resource.name || 'resource'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
    }
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading resources: {error.message}
      </div>
    )
  }

  return (
    <div className="container-fluid px-4 py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1">Resource Center</h1>
          <p className="text-muted mb-0">
            Manage tutorials, documents, templates, and helpful resources
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className={`btn btn-sm ${showFolders ? 'btn-secondary' : 'btn-outline-secondary'}`}
            onClick={() => setShowFolders(!showFolders)}
            title={`${showFolders ? 'Hide' : 'Show'} folders`}
          >
            <i className="bi bi-folder" />
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            title={`Switch to ${viewMode === 'table' ? 'grid' : 'table'} view`}
          >
            <i className={`bi ${viewMode === 'table' ? 'bi-grid-3x3' : 'bi-list'}`} />
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onOpenManageTags}
            title="Create and rename resource tags"
          >
            <i className="bi bi-tags me-1" /> Manage Tags
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onOpenAdd}>
            <i className="fa fa-plus me-1" /> Add Resource
          </button>
        </div>
      </div>

      <div className="row g-3">
        {/* Folder tree sidebar — sticky: queda visible mientras se scrollea la lista */}
        {showFolders && (
          <div className="col-12 col-md-4 col-lg-3">
            <div
              style={{
                position: 'sticky',
                top: 'calc(var(--bs-app-header-height, 70px) + 1rem)',
              }}
            >
              <CategoryTree
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={handleSelectCategory}
                onCategoriesLoaded={handleCategoriesLoaded}
                onDropResources={handleDropResources}
                onRefetchAvailable={handleCategoryTreeRefetchAvailable}
              />
            </div>
          </div>
        )}

        <div className={showFolders ? 'col-12 col-md-8 col-lg-9' : 'col-12'}>
      {/* Bloque sticky: breadcrumb + filtros + barra de selección quedan fijos
          y solo la lista de archivos scrollea */}
      <div
        style={{
          position: 'sticky',
          // Pegado exactamente al header (sin gap) y con padding interno:
          // así el contenido que scrollea nunca se ve pasar por detrás
          top: 'var(--bs-app-header-height, 70px)',
          zIndex: 5,
          backgroundColor: 'var(--bs-app-bg-color, #f5f8fa)',
          paddingTop: '1rem',
          marginTop: '-1rem',
        }}
      >
      {/* Breadcrumb de carpeta seleccionada */}
      {selectedCategoryId && (
        <nav aria-label="breadcrumb" className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none"
                onClick={() => handleSelectCategory(null)}
              >
                <i className="bi bi-collection me-1" /> All resources
              </button>
            </li>
            {categoryPath.map((category, idx) => (
              <li
                key={category.id}
                className={`breadcrumb-item ${idx === categoryPath.length - 1 ? 'active' : ''}`}
              >
                {idx === categoryPath.length - 1 ? (
                  <span>
                    <i className="bi bi-folder2-open me-1 text-warning" />
                    {category.name}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => handleSelectCategory(category.id)}
                  >
                    {category.name}
                  </button>
                )}
              </li>
            ))}
          </ol>
          {hasActiveSearch ? (
            <span className="badge bg-light text-dark border small">
              <i className="bi bi-search me-1" />
              Searching in this folder and its subfolders
            </span>
          ) : (
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeSubfolders"
                checked={includeSubcategories}
                onChange={(e) => setIncludeSubcategories(e.target.checked)}
              />
              <label className="form-check-label small text-muted" htmlFor="includeSubfolders">
                Include subfolders
              </label>
            </div>
          )}
        </nav>
      )}

      {/* Filters Card */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md">
              <label className="form-label fw-bold">Search by name</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type to search..."
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
                <span style={{ position: 'absolute', right: '10px', top: '8px' }}>
                  <i className="bi bi-search" />
                </span>
              </div>
            </div>

            <div className="col-12 col-md">
              <label className="form-label fw-bold">Filter by type</label>
              <Select
                isClearable
                options={RESOURCE_TYPE_OPTIONS}
                onChange={(option) => setFilterType(option?.value || null)}
                placeholder="All types"
                styles={{
                  control: (base) => ({ ...base, padding: '2px' }),
                }}
              />
            </div>

            <div className="col-12 col-md">
              <label className="form-label fw-bold">Filter by tag</label>
              <Select
                isClearable
                options={
                  dataTags?.allAttachmentsTags.edges.map(({ node }) => ({
                    label: node.tagName,
                    value: node.tagName,
                  })) || []
                }
                onChange={(option) => setFilterTag(option?.value || null)}
                placeholder="All tags"
                styles={{
                  control: (base) => ({ ...base, padding: '2px' }),
                }}
              />
            </div>

            <div className="col-12 col-md-auto d-flex align-items-end justify-content-md-end ms-md-auto">
              <button
                className="btn btn-primary btn-sm w-100"
                style={{ minWidth: 140 }}
                onClick={handleSearchClick}
              >
                <i className="bi bi-search me-2" /> Search
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Selection toolbar */}
      {selectedResourceIds.length > 0 && (
        <div className="alert alert-primary d-flex justify-content-between align-items-center py-2 mb-3">
          <span>
            <i className="bi bi-check2-square me-2" />
            <strong>{selectedResourceIds.length}</strong> selected —{' '}
            <i className="bi bi-arrows-move mx-1" /> drag to a folder to move
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => setSelectedResourceIds([])}
          >
            <i className="bi bi-x-lg me-1" /> Clear selection
          </button>
        </div>
      )}

      {/* Fila única: page size + contador de resultados */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-2">
        <label className="d-flex align-items-center gap-2 mb-0">
          Show
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value, 10)
              setPageSize(newSize === -1 ? totalResources : newSize)
              setPage(1)
            }}
            style={{ width: 'auto' }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={-1}>All</option>
          </select>
          entries
        </label>
        <span className="text-muted small d-flex align-items-center gap-2">
          {loading && (
            <span className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Searching...</span>
            </span>
          )}
          {totalResources === 0
            ? 'Showing 0 entries'
            : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(
                page * pageSize,
                totalResources
              )} of ${totalResources} ${
                debouncedFilterName || filterType || debouncedFilterTag
                  ? 'filtered results'
                  : selectedCategoryId
                  ? 'resources in this folder'
                  : 'uncategorized resources'
              }`}
        </span>
      </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        // Table View
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        title="Select all on this page"
                        checked={
                          paginatedResources.length > 0 &&
                          paginatedResources.every((r) => selectedResourceIds.includes(r.id))
                        }
                        onChange={(e) => {
                          const pageIds = paginatedResources.map((r) => r.id)
                          setSelectedResourceIds((prev) =>
                            e.target.checked
                              ? Array.from(new Set([...prev, ...pageIds]))
                              : prev.filter((id) => !pageIds.includes(id))
                          )
                        }}
                      />
                    </th>
                    <th style={{ width: 50 }}></th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Created By</th>
                    <th style={{ width: 100 }}>Views</th>
                    <th style={{ width: 150 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Subcarpetas de la carpeta actual: click navega, drop mueve el recurso.
                      Ocultas mientras hay una búsqueda activa (vista plana de resultados) */}
                  {!hasActiveSearch && currentFolders.map((folder) => (
                    <tr
                      key={`folder-${folder.id}`}
                      onClick={() => handleSelectCategory(folder.id)}
                      className={dragOverFolderId === folder.id ? 'bg-warning bg-opacity-25' : ''}
                      style={{ cursor: 'pointer' }}
                      title="Open folder · Drop resources here to move them"
                      onDragOver={(e) => {
                        if (e.dataTransfer.types.includes('application/x-resource-ids')) {
                          e.preventDefault()
                          setDragOverFolderId(folder.id)
                        }
                      }}
                      onDragLeave={() => setDragOverFolderId(null)}
                      onDrop={(e) => {
                        e.preventDefault()
                        setDragOverFolderId(null)
                        const raw = e.dataTransfer.getData('application/x-resource-ids')
                        if (raw) handleDropResources(folder.id, JSON.parse(raw))
                      }}
                    >
                      <td />
                      <td>
                        <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="bi bi-folder-fill fs-3 text-warning" />
                        </div>
                      </td>
                      <td colSpan={5} className="align-middle">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold">{folder.name}</span>
                            <span className="badge bg-light text-muted">
                              {folder.resourcesCount} item{folder.resourcesCount === 1 ? '' : 's'}
                            </span>
                          </div>
                          <i className="bi bi-chevron-right text-muted" />
                        </div>
                      </td>
                    </tr>
                  ))}

                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedResources.length === 0 && (hasActiveSearch || currentFolders.length === 0) ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5">
                        <div className="d-flex flex-column align-items-center">
                          <i className="bi bi-folder2-open" style={{ fontSize: 48, color: '#ccc' }} />
                          <h5 className="mt-3 text-muted">No resources found</h5>
                          <p className="text-muted">Try adjusting your filters or add a new resource</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedResources.map((resource) => (
                      <tr
                        key={resource.id}
                        draggable
                        onDragStart={(e) => handleResourceDragStart(e, resource)}
                        className={selectedResourceIds.includes(resource.id) ? 'table-active' : ''}
                        title="Drag to a folder to move"
                      >
                        <td className="align-middle">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            style={{ cursor: 'pointer' }}
                            title="Select to move"
                            checked={selectedResourceIds.includes(resource.id)}
                            onChange={() => toggleResourceSelection(resource.id)}
                          />
                        </td>
                        <td>
                          {resource.thumbnail ? (
                            <img
                              src={prependBackendUrl(resource.thumbnail)}
                              alt=""
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                            />
                          ) : resource.url ? (
                            <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <i className="bi bi-link-45deg fs-3 text-primary" />
                            </div>
                          ) : (
                            <RenderFilePreview file={prependBackendUrl(resource.file || '')} />
                          )}
                        </td>
                        <td className="align-middle">
                          <div className="d-flex align-items-center gap-2">
                            <span>{getResourceTypeIcon(resource.resourceType)}</span>
                            {/* Click en el título = abrir visualización */}
                            <span
                              className="fw-bold text-hover-primary"
                              style={{ cursor: 'pointer' }}
                              title="View resource"
                              onClick={() => handleResourceAccess(resource, 'view')}
                            >
                              {resource.name}
                            </span>
                          </div>
                          {/* Debajo del nombre: línea de tags (badge outline) y
                              debajo la ruta de carpetas (la descripción se ve al abrir) */}
                          {resource.documentTag && (
                            <div className="mt-1">
                              <span className="badge badge-light-primary border border-primary">
                                <i className="bi bi-tag me-1" />
                                {resource.documentTag.tagName}
                              </span>
                            </div>
                          )}
                          {resource.category && (
                            <small className="text-muted d-block mt-1">
                              <i className="bi bi-folder me-1 text-warning" />
                              {(() => {
                                const path = getCategoryPath(allCategories, resource.category.id)
                                return path.length > 0
                                  ? path.map((c) => c.name).join(' / ')
                                  : resource.category.name
                              })()}
                            </small>
                          )}
                        </td>
                        <td className="align-middle">
                          <span style={getResourceTypeBadgeStyle(resource.resourceType)}>
                            {getResourceTypeLabel(resource.resourceType)}
                          </span>
                        </td>
                        <td className="align-middle">
                          {resource.user ? (
                            <div>
                              <div className="fw-semibold">
                                {resource.user.firstName} {resource.user.lastName}
                              </div>
                              <small className="text-muted">
                                {moment(resource.created).format('MMM DD, YYYY')}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="align-middle text-center">
                          <ViewsBadge viewsCount={resource.viewsCount} />
                        </td>
                        <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-2">
                            {!resource.url && (
                              <button
                                onClick={() => handleResourceAccess(resource, 'download')}
                                className="btn btn-sm btn-outline-success"
                                title="Download"
                              >
                                <i className="bi bi-download" />
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-warning"
                              title="Edit"
                              onClick={() => handleEdit(resource)}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Delete"
                              onClick={() => handleDelete(resource)}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="row mt-3">
              <div className="col-sm-12 col-md-5">
                <div className="text-muted">
                  {totalResources === 0
                    ? 'Showing 0 entries'
                    : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(
                      page * pageSize,
                      totalResources
                    )} of ${totalResources} entries`}
                </div>
              </div>
              <div className="col-sm-12 col-md-7">
                <div className="d-flex justify-content-end">
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <i className="la la-angle-left" />
                      </button>
                    </li>
                    <li className="page-item">
                      <span className="page-link">
                        Page {page} of {totalPages}
                      </span>
                    </li>
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <i className="la la-angle-right" />
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Grid View
        <div className="row g-3">
          {/* Subcarpetas de la carpeta actual: click navega, drop mueve el recurso.
              Ocultas mientras hay una búsqueda activa (vista plana de resultados) */}
          {!hasActiveSearch && currentFolders.map((folder) => (
            <div key={`folder-${folder.id}`} className="col-md-6 col-lg-4 col-xl-3">
              <div
                className={`card h-100 shadow-sm hover-shadow ${
                  dragOverFolderId === folder.id ? 'border border-warning border-2 bg-warning bg-opacity-10' : ''
                }`}
                style={{ cursor: 'pointer' }}
                title="Open folder · Drop resources here to move them"
                onClick={() => handleSelectCategory(folder.id)}
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes('application/x-resource-ids')) {
                    e.preventDefault()
                    setDragOverFolderId(folder.id)
                  }
                }}
                onDragLeave={() => setDragOverFolderId(null)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragOverFolderId(null)
                  const raw = e.dataTransfer.getData('application/x-resource-ids')
                  if (raw) handleDropResources(folder.id, JSON.parse(raw))
                }}
              >
                <div
                  style={{
                    height: 200,
                    backgroundColor: '#fffbeb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className="bi bi-folder-fill" style={{ fontSize: 72, color: '#ffc107' }} />
                </div>
                <div className="card-body text-center">
                  <h6 className="card-title mb-1">{folder.name}</h6>
                  <small className="text-muted">
                    {folder.resourcesCount} item{folder.resourcesCount === 1 ? '' : 's'}
                    {folder.childrenCount > 0 &&
                      ` · ${folder.childrenCount} subfolder${folder.childrenCount === 1 ? '' : 's'}`}
                  </small>
                </div>
              </div>
            </div>
          ))}

          {loading ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : paginatedResources.length === 0 && (hasActiveSearch || currentFolders.length === 0) ? (
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="bi bi-folder2-open" style={{ fontSize: 48, color: '#ccc' }} />
                  <h5 className="mt-3 text-muted">No resources found</h5>
                  <p className="text-muted">Try adjusting your filters or add a new resource</p>
                </div>
              </div>
            </div>
          ) : (
            paginatedResources.map((resource) => (
              <div key={resource.id} className="col-md-6 col-lg-4 col-xl-3">
                <div
                  className={`card h-100 shadow-sm hover-shadow ${
                    selectedResourceIds.includes(resource.id) ? 'border border-primary border-2' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleResourceDragStart(e, resource)}
                  onClick={() => handleResourceAccess(resource, 'view')}
                  style={{ cursor: 'pointer' }}
                  title="Click to view · Drag to a folder to move"
                >
                  {/* Preview: thumbnail si existe; al hover, si es video se
                      reproduce en silencio (estilo YouTube) */}
                  <div
                    style={{
                      height: 200,
                      backgroundColor: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={() => setHoveredResourceId(resource.id)}
                    onMouseLeave={() => setHoveredResourceId(null)}
                  >
                    {(() => {
                      const isHovered = hoveredResourceId === resource.id
                      const fileName = resource.file?.split('/').pop()?.split('?')[0] || ''
                      const isEmbeddableVideo = resource.url ? Boolean(parseVideoUrl(resource.url)) : false
                      const isFileVideo = Boolean(resource.file) && isVideoFile(fileName)

                      if (isHovered && isEmbeddableVideo) {
                        return (
                          // pointerEvents none: el iframe no roba el mouse, así el
                          // hover/click de la card siguen funcionando
                          <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
                            <VideoPreview url={resource.url!} height="100%" autoPlay />
                          </div>
                        )
                      }
                      if (isHovered && isFileVideo) {
                        return (
                          <video
                            src={prependBackendUrl(resource.file || '')}
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                          />
                        )
                      }
                      if (resource.thumbnail) {
                        return (
                          <img
                            src={prependBackendUrl(resource.thumbnail)}
                            alt={resource.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )
                      }
                      if (resource.url) {
                        return (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="bi bi-link-45deg" style={{ fontSize: 64, color: '#0d6efd' }} />
                          </div>
                        )
                      }
                      return <GridFilePreview file={prependBackendUrl(resource.file || '')} />
                    })()}

                    {/* Selection checkbox */}
                    <div
                      style={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedResourceIds.includes(resource.id)}
                        onChange={() => toggleResourceSelection(resource.id)}
                      />
                    </div>

                    {/* Type badge */}
                    <div style={{ position: 'absolute', top: 10, left: 40 }}>
                      <span style={getResourceTypeBadgeStyle(resource.resourceType)}>
                        {getResourceTypeIcon(resource.resourceType)}{' '}
                        {getResourceTypeLabel(resource.resourceType)}
                      </span>
                    </div>

                    {/* Views badge */}
                    <div style={{ position: 'absolute', top: 10, right: 10 }}>
                      <ViewsBadge viewsCount={resource.viewsCount} />
                    </div>
                  </div>

                  <div className="card-body">
                    {/* Solo el título — la descripción se ve al abrir el recurso */}
                    <h6 className="card-title mb-2">{resource.name}</h6>

                    {(resource.documentTag || resource.category) && (
                      <div className="mb-2 d-flex flex-wrap gap-1">
                        {resource.documentTag && (
                          <span className="badge bg-secondary">{resource.documentTag.tagName}</span>
                        )}
                        {resource.category && (
                          <span className="badge bg-light text-dark border">
                            <i className="bi bi-folder me-1 text-warning" />
                            {resource.category.name}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center text-muted small">
                      <span>
                        {resource.user
                          ? `${resource.user.firstName} ${resource.user.lastName}`
                          : 'Unknown'}
                      </span>
                      <span>{moment(resource.created).format('MMM DD, YYYY')}</span>
                    </div>
                  </div>

                  <div className="card-footer bg-white border-top-0" onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex gap-2">
                      <button
                        onClick={() => handleResourceAccess(resource, 'view')}
                        className="btn btn-sm btn-primary flex-fill"
                      >
                        <i className="bi bi-eye me-1" /> View
                      </button>
                      {!resource.url && (
                        <button
                          onClick={() => handleResourceAccess(resource, 'download')}
                          className="btn btn-sm btn-outline-success"
                          title="Download"
                        >
                          <i className="bi bi-download" />
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => handleEdit(resource)}
                      >
                        <i className="bi bi-pencil" />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(resource)}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Pagination in grid view */}
          {!loading && paginatedResources.length > 0 && (
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {(page - 1) * pageSize + 1} to{' '}
                      {Math.min(page * pageSize, totalResources)} of {totalResources} entries
                    </div>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                        >
                          <i className="la la-angle-left" />
                        </button>
                      </li>
                      <li className="page-item">
                        <span className="page-link">
                          Page {page} of {totalPages}
                        </span>
                      </li>
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                        >
                          <i className="la la-angle-right" />
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
        </div>
      </div>

      {/* Modals */}
      {isOpenAdd && (
        <ModalAddResource
          isOpen={isOpenAdd}
          onClose={onCloseAdd}
          refetch={handleRefetch}
          defaultCategoryId={selectedCategoryId}
        />
      )}

      {isOpenEdit && selectedResource && (
        <ModalEditResource
          isOpen={isOpenEdit}
          onClose={onCloseEdit}
          refetch={handleRefetch}
          resource={selectedResource}
        />
      )}

      {isOpenDelete && selectedResource && (
        <ModalDeleteResource
          isOpen={isOpenDelete}
          onClose={onCloseDelete}
          refetch={handleRefetch}
          resource={selectedResource}
        />
      )}

      {isOpenView && selectedResource && (
        <ModalViewResource
          isOpen={isOpenView}
          onClose={onCloseView}
          resource={selectedResource}
        />
      )}

      {isOpenManageTags && (
        <ModalManageTags isOpen={isOpenManageTags} onClose={onCloseManageTags} />
      )}
    </div>
  )
}
